import { Injectable, inject } from '@angular/core';
import { CompositeFilterDescriptor, FilterDescriptor, GroupDescriptor, SortDescriptor, State } from '@progress/kendo-data-query';
import { Table } from 'app/shared/models';
import { NameValueInt } from 'app/shared/models/name-value';
import { BehaviorSubject, Observable } from 'rxjs';
import { ColumnMap } from './column-map';
import { OdataColumnMapper } from './column-mapper';
import { DataApi } from './data-api.service';
import { LookupMapper } from './lookup-mapper';

@Injectable({
  providedIn: 'root',
})
export class DataLookupService {
  dataApi = inject(DataApi);

  private _Store: Map<string, any> = new Map();
  private _hasDirty = false;
  private _ValueStore: Map<Table, Map<number, string>> = new Map();

  get hasDirty(): boolean {
    return this._hasDirty;
  }

  private isIterable(obj) {
    if (obj === null) {
      return false;
    }
    return typeof obj[Symbol.iterator] === 'function';
  }

  public getNameAsync(tableprop: Table | string, fieldName: string, model: any, modelField: string): Observable<any> {
    if (!modelField) {
      modelField = fieldName + 'ValueName$$';
    }
    if (!model) {
      return Observable.create();
    }
    let cached = model[modelField];
    if (cached !== undefined) {
      return BehaviorSubject.create('');
    }
    const fieldValue: number = model[fieldName];
    if (fieldValue === undefined || fieldValue === null) {
      model[modelField] = '';
      return BehaviorSubject.create('');
    }

    let table: Table = tableprop as Table;
    const tableName: string = tableprop as string;

    if (typeof tableprop === 'string') {
      table = Table[tableName];
      if (!table) {
        throw Error(
          'The tablename [' + tableprop + '] passed to LookupService.get is not known.' +
          'You must use a table from the tableNames Enum'
        );
      }
    }
    let values = this._ValueStore.get(table);
    if (values) {
      const name = values.get(fieldValue);
      if (name) {
        cached = model[modelField] = name;
        return BehaviorSubject.create(cached);
      }
    }

    const lookup = this.get(table);
    const me = this;
    let result = new BehaviorSubject('');
    const found = lookup.then((d) => {
      if (d && d.length) {
        if (!values) {
          values = new Map<number, string>();
          me._ValueStore.set(table, values);
        }
        let isSet = false;
        d.forEach(f => {
          if (!isSet && f.value === fieldValue) {
            cached = f.name;
            isSet = true;
          }
          values.set(f.value, f.name);
        });
        if (cached) { model[modelField] = cached; }
        result.next(cached);
      }
    });
    return result;
  }

  public getName(tableprop: Table | string, fieldName: string, model: any, modelField: string): any {
    if (!modelField) {
      modelField = fieldName + 'ValueName$$';
    }
    let cached = model[modelField];
    if (cached) {
      return cached;
    }

    const fieldValue: number = model[fieldName];
    if (fieldValue === undefined) {
      model[modelField] = '';
      return '';
    }

    let table: Table = tableprop as Table;
    const tableName: string = tableprop as string;

    if (typeof tableprop === 'string') {
      table = Table[tableName];
      if (!table) {
        throw Error(
          'The tablename [' + tableprop + '] passed to LookupService.get is not known.' +
          'You must use a table from the tableNames Enum'
        );
      }
    }
    let values = this._ValueStore.get(table);
    if (values) {
      const name = values.get(fieldValue);
      if (name) {
        cached = model[modelField] = name;
        return cached;
      }
    }

    const lookup = this.get(table);
    const me = this;
    const found = lookup.then((d) => {
      if (d && d.length) {
        if (!values) {
          values = new Map<number, string>();
          me._ValueStore.set(table, values);
        }
        let isSet = false;
        d.forEach(f => {
          if (!isSet && f.value === fieldValue) {
            cached = f.name;
            isSet = true;
          }
          values.set(f.value, f.name);
        });
        if (cached) {
          model[modelField] = cached;
        }
      }
    });
    return cached;
  }

  public getNameValue(tableprop: Table | string, stateOrFilter?: State | string, lookup: LookupMapper = null, useCache = false): Promise<NameValueInt[]> {
    let table: Table = tableprop as Table;
    let tableName: string = tableprop as string;
    if (typeof tableprop === 'string') {
      table = Table[tableName];
      if (!table) {
        throw Error(
          `The tablename [${tableprop}] passed to LookupService.get is not known. You must use a table from the Table Enum`
        );
      }
    } else {
      tableName = Table[tableprop];
    }
    if (!lookup && OdataColumnMapper.Store && OdataColumnMapper.Store.has(table)) {
      lookup = OdataColumnMapper.Store.get(table);
    } else if (!lookup) {
      lookup = new LookupMapper(table);
      OdataColumnMapper.Store.set(table, lookup);
    }

    const result = this.dataApi.get(table, lookup, true, stateOrFilter, useCache ? this._Store : null);

    return result;
  }

  public get(
    tableprop: Table | string,
    columnMaps?: ColumnMap[] | boolean,
    limitColumnsToMap: boolean = true,
    ignoreCache: boolean = false,
    stateOrQuery?: State | string,
  ): Promise<any> {

    let table: Table = tableprop as Table;
    let tableName: string = tableprop as string;

    if (typeof tableprop === 'string') {
      table = Table[tableName];
      if (!table) {
        throw Error(
          `The tablename [${tableprop}] passed to LookupService.get is not known. You must use a table from the Table Enum`
        );
      }
    } else {
      tableName = Table[tableprop];
    }

    let lookup: LookupMapper;

    if ((columnMaps === null || columnMaps === undefined) &&
      OdataColumnMapper.Store && OdataColumnMapper.Store.has(table)) {
      lookup = OdataColumnMapper.Store.get(table);
    } else if (columnMaps === true) {
      lookup = new LookupMapper(table);
      OdataColumnMapper.Store.set(table, lookup);
    } else {
      const maps = columnMaps as ColumnMap[];    // might be null, that's ok

      lookup = new LookupMapper(table, maps);

      if (maps) {
        OdataColumnMapper.Store.set(table, lookup);
      }
    }
    const result = this.dataApi.get(table, lookup, limitColumnsToMap, stateOrQuery, ignoreCache ? null : this._Store);

    return result;
  }

  public buildState(skip: number = 0, take?: number, sort?: Array<SortDescriptor>,
    filter?: CompositeFilterDescriptor, group?: Array<GroupDescriptor>): State {
    let result: State = {
      skip: skip,
      take: take,
      sort: sort,
      filter: filter,
      group: group
    };
    return result;
  }

  public createFD(field: string | Function, operator: FilterOperators | Function, value?: any, ignoreCase?: boolean): FilterDescriptor {
    return {
      field: field,
      operator: operator,
      value: value,
      ignoreCase: ignoreCase
    };
  }

  public buildOrFilters(fieldName: string, items: any[], entityValueMap: string = null): CompositeFilterDescriptor {
    let filters: Array<FilterDescriptor> = [];

    for (let i = 0; i < items.length; i++) {
      let actual = entityValueMap ? items[i][entityValueMap] : items[i];
      filters.push(this.createFD(fieldName, 'eq', actual));
    }
    const result: CompositeFilterDescriptor = {
      logic: 'or',
      filters: filters
    };
    return result;
  }
  ///combines into one composite filter descriptor, with a filters array of Filter Descriptors
  public getFilters(dest: Array<FilterDescriptor>, ...filters: Array<CompositeFilterDescriptor>) {
    for (let i = 0; i < filters.length; i++) {
      let one = filters[i];
      if (one &&
        one.filters &&
        one.filters.length) {
        for (let ii = 0; ii < one.filters.length; ii++) {
          let two = one.filters[ii];
          if ((two as CompositeFilterDescriptor).filters) {
            this.getFilters(dest, ...one.filters as Array<CompositeFilterDescriptor>);
          } else {
            dest.push(two as FilterDescriptor);
          }
        }
      }
    }
  }

  public combineFilters(logic: 'and' | 'or' = 'and', ...filters: Array<FilterDescriptor | CompositeFilterDescriptor>): CompositeFilterDescriptor {
    let output: CompositeFilterDescriptor;
    if (!filters || !filters.length) {
      return null;
    }

    let allFds: Array<FilterDescriptor> = [];
    this.getFilters(allFds, ...filters as Array<CompositeFilterDescriptor>);

    //remove duplicates from allFds
    let m: Map<string, FilterDescriptor> = new Map();

    for (let i = 0; i < allFds.length; i++) {
      let one = allFds[i];
      let fn: string;
      if (typeof one.field === 'function') {
        let func = one.field as Function;
        fn = (func.call(this)) as string;
      } else {
        fn = one.field as string;
      }
      m.set(fn, allFds[i]);
    }
    let keys = Array.from(m.keys());

    let outputFilters: Array<FilterDescriptor> = new Array();
    for (let k = 0; k < keys.length; k++) {
      outputFilters.push(m.get(keys[k]));
    }

    output = {
      logic: logic,
      filters: outputFilters
    };
    return output;
  }

  // TOOD: Remove unused parameters (and update calls accordingly)
  public buildFilter(
    formValues: any, logic: 'and' | 'or' = 'and',
    entityValueMap: string = null, includeNulls: boolean = false,
    multiValueLogic: 'and' | 'or' = 'or'
  ): FilterDescriptor | CompositeFilterDescriptor {
    if (!formValues) { return null; }
    const filters: Array<FilterDescriptor | CompositeFilterDescriptor> = [];
    if (formValues) {
      for (const entry in formValues) {
        const name = entry;
        let value = formValues[entry];
        if (value || includeNulls) {
          if (this.isIterable(value)) {
            let list = Array.from(value) as any[];
            if (list && list.length && list.length > 1) {
              let f = this.buildOrFilters(entry as string, list, entityValueMap);
              filters.push(f);
              continue;   // TOP OF FOR LOOP
            } else if (list && list.length === 1) {
              value = value[0];
            }
          }
          let actualValue = entityValueMap ? value[entityValueMap] : value;
          const filter: FilterDescriptor = this.createFD(entry, 'eq', actualValue);
          if (typeof value === 'string') {
            filter.operator = 'contains';
            filter.ignoreCase = true;
          } else {
            filter.operator = 'eq';
          }
          filters.push(filter);
        }
      }
    }

    const result = {
      logic: logic,
      filters: filters
    };
    return result;
  }
}

export type GeneralFilterOperators = 'eq' | 'neq' | 'isnull' | 'isnotnull' | 'lt' | 'lte' | 'gt' | 'gte';
export type StringFilterOperators = 'startswith' | 'endswith' | 'contains' | 'doesnotcontain' | 'isempty' | 'isnotempty';
export type FilterOperators = GeneralFilterOperators | StringFilterOperators;